// Must match the resource key case-sensitive as defined in WorkOS FGA
export enum ResourceType {
	PLATFORM = 'platform',
	USER = 'user',
	DEAL = 'deal',
	PROGRAMME = 'programme',
	ORGANIZATION = 'organization',
}

// Must match the resource key case-sensitive as defined in WorkOS FGA
export enum PermissionRole {
	ADMIN = 'admin',
	USER_ADMIN = 'userAdmin',
	KYC_ADMIN = 'kycAdmin',
	EDITOR = 'editor',
	EXPORT_VIEWER = 'exportViewer',
	VIEWER = 'viewer',
}

export enum FGAInternalRole {
	PROGRAMME_USER = 'programmeUser',
	DEALSTACK_SUPPORT = 'dealstackSupport',
	INVESTOR = 'investor',
}

export enum InheritanceRelation {
	CHILD = 'child',
	PARENT = 'parent',
	INTEGRATED_WITH = 'integrated_with',
}

export const OWNER_ROLE = 'owner'; // Internal representation of OWNER role

export type PermissionRoleWithOwner = PermissionRoleAndInternalRole | typeof OWNER_ROLE;

export const PermissionRoleWithOwnerEnum = {
	...PermissionRole,
	OWNER: OWNER_ROLE,
} as const;

export type PermissionRoleAndInternalRole = PermissionRole | FGAInternalRole;

export type PermissionRoleOrInvestor = PermissionRole | FGAInternalRole.INVESTOR;

type PermissionConfig = {
	priority: number;
	inherits: PermissionRole[];
	crossResourceGrants: CrossResourceInheritance[];
};

type PermissionsMap = {
	[K in PermissionRole]?: PermissionConfig;
};

type ResourceConfig = {
	permissions: PermissionsMap;
};

type Schema = {
	[K in ResourceType]: ResourceConfig;
};

type CrossResourceInheritance = {
	grantsResourceType: ResourceType; // the resource type where the permission originates
	grantsRole: PermissionRole; // the role on that resource
};

// This schema must match what is in the WorkOS FGA schema.
// The order of resources matters — it determines how they’re displayed on the frontend when configuring user permissions.”
export const SCHEMA: Schema = {
	[ResourceType.PLATFORM]: {
		permissions: {
			[PermissionRole.VIEWER]: {
				priority: 1,
				inherits: [],
				crossResourceGrants: [],
			},
		},
	},
	[ResourceType.DEAL]: {
		permissions: {
			[PermissionRole.ADMIN]: {
				priority: 1,
				inherits: [PermissionRole.EDITOR, PermissionRole.USER_ADMIN],
				crossResourceGrants: [
					{
						grantsResourceType: ResourceType.PROGRAMME,
						grantsRole: PermissionRole.ADMIN,
					},
				],
			},
			[PermissionRole.EDITOR]: {
				priority: 2,
				inherits: [PermissionRole.VIEWER, PermissionRole.EXPORT_VIEWER],
				crossResourceGrants: [
					{
						grantsResourceType: ResourceType.PROGRAMME,
						grantsRole: PermissionRole.VIEWER,
					},
				],
			},
			[PermissionRole.USER_ADMIN]: {
				priority: 2,
				inherits: [PermissionRole.VIEWER],
				crossResourceGrants: [],
			},
			[PermissionRole.EXPORT_VIEWER]: {
				priority: 3,
				inherits: [PermissionRole.VIEWER],
				crossResourceGrants: [],
			},
			[PermissionRole.VIEWER]: {
				priority: 4,
				inherits: [],
				crossResourceGrants: [],
			},
		},
	},

	[ResourceType.ORGANIZATION]: {
		permissions: {
			[PermissionRole.ADMIN]: {
				priority: 1,
				inherits: [PermissionRole.USER_ADMIN],
				crossResourceGrants: [],
			},
			[PermissionRole.USER_ADMIN]: {
				priority: 2,
				inherits: [PermissionRole.VIEWER],
				crossResourceGrants: [],
			},
			[PermissionRole.VIEWER]: {
				priority: 3,
				inherits: [],
				crossResourceGrants: [
					{
						grantsResourceType: ResourceType.PLATFORM,
						grantsRole: PermissionRole.VIEWER,
					},
				],
			},
		},
	},
	[ResourceType.PROGRAMME]: {
		permissions: {
			[PermissionRole.ADMIN]: {
				priority: 1,
				inherits: [
					PermissionRole.EDITOR,
					PermissionRole.USER_ADMIN,
					PermissionRole.KYC_ADMIN,
				],
				crossResourceGrants: [],
			},
			[PermissionRole.EDITOR]: {
				priority: 2,
				inherits: [PermissionRole.VIEWER, PermissionRole.EXPORT_VIEWER],
				crossResourceGrants: [],
			},

			[PermissionRole.USER_ADMIN]: {
				priority: 2,
				inherits: [PermissionRole.VIEWER],
				crossResourceGrants: [],
			},
			[PermissionRole.KYC_ADMIN]: {
				priority: 2,
				inherits: [PermissionRole.VIEWER, PermissionRole.EXPORT_VIEWER],
				crossResourceGrants: [],
			},
			[PermissionRole.EXPORT_VIEWER]: {
				priority: 3,
				inherits: [PermissionRole.VIEWER],
				crossResourceGrants: [],
			},
			[PermissionRole.VIEWER]: {
				priority: 4,
				inherits: [],
				crossResourceGrants: [],
			},
		},
	},
	[ResourceType.USER]: {
		permissions: {},
	},
};

export const getResourceTypePermissions = (resourceType: ResourceType) =>
	Object.keys(SCHEMA[resourceType].permissions) as PermissionRole[];

export const getCrossResourceGrantTargets = (
	resourceType: ResourceType,
	permissionRole: PermissionRoleWithOwner
): ResourceType[] => {
	if (!isPermissionRole(permissionRole)) {
		return [];
	}

	const resourcePermissions = SCHEMA[resourceType].permissions;
	const config = resourcePermissions[permissionRole];

	if (!config) {
		return [];
	}

	const grants = config.crossResourceGrants;

	return grants.map((g) => g.grantsResourceType);
};

export const getResourceTypePermissionsWithOwner = (
	resourceType: ResourceType
): PermissionRoleWithOwner[] => {
	const baseRoles = getResourceTypePermissions(resourceType);
	return [OWNER_ROLE, ...baseRoles];
};

export type Permission = {
	resourceType: ResourceType;
	resourceId: string;
	permissionRole: PermissionRoleAndInternalRole;
	isImplicit: boolean;
};

export type PermissionWithOwner = {
	resourceType: ResourceType;
	resourceId: string;
	permissionRole: PermissionRoleWithOwner;
	isImplicit: boolean;
};

export const isPermissionRole = (role: PermissionRoleWithOwner): role is PermissionRole => {
	return Object.values(PermissionRole).includes(role as PermissionRole);
};

export const getPermissionPriority = (
	permissionRole: PermissionRole,
	resourceType: ResourceType
): number | undefined => {
	const resourceRelations = SCHEMA[resourceType].permissions;
	const priority = resourceRelations[permissionRole]?.priority;

	return priority;
};

export const getLowestPermissionRole = (resourceType: ResourceType): PermissionRole | undefined => {
	const permissions = SCHEMA[resourceType].permissions;

	let lowest: { role: PermissionRole; priority: number } | undefined;

	for (const [role, config] of Object.entries(permissions) as [
		PermissionRole,
		PermissionConfig,
	][]) {
		if (config.priority !== undefined) {
			if (!lowest || config.priority > lowest.priority) {
				lowest = { role, priority: config.priority };
			}
		}
	}

	return lowest?.role;
};

export interface InvestorFGAPayload {
	userId: string;
	mipId: string;
}

export interface InvestorsBulkFGAPayload {
	investors: Array<{ userId: string; mipId: string }>;
}

export interface InvestorUnlinkedFGAPayload {
	userIds: string[];
	mipId: string;
}
